import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Form, Modal, Table } from 'react-bootstrap';
import { FaEdit, FaTrash } from 'react-icons/fa';
import adminSchemaApi from '../api/adminSchemaApi';
import '../styles/admin-theme.css';

const AdminLicenseTypes = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ code: '', name: '', description: '', is_system: false, category: '', license_capability: '' });
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadList = useCallback(async () => {
    try {
      setLoading(true);
      const data = await adminSchemaApi.fetchLicenseTypes();
      setList(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.message || 'Failed to load license types');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const openCreate = () => {
    setEditing(null);
    setForm({ code: '', name: '', description: '', is_system: false, category: '', license_capability: '' });
    setShowModal(true);
    setError('');
  };

  const openEdit = (row) => {
    setEditing(row);
    const cap = row.license_capability;
    setForm({
      code: row.code || '',
      name: row.name || '',
      description: row.description || '',
      is_system: !!row.is_system,
      category: row.category || '',
      license_capability: cap != null ? (typeof cap === 'string' ? cap : JSON.stringify(cap, null, 2)) : '',
    });
    setShowModal(true);
    setError('');
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditing(null);
    setForm({ code: '', name: '', description: '', is_system: false, category: '', license_capability: '' });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.code?.trim() || !form.name?.trim()) {
      setError('Code and name are required.');
      return;
    }
    let license_capability = null;
    if (form.license_capability?.trim()) {
      try {
        license_capability = JSON.parse(form.license_capability.trim());
      } catch {
        setError('License capability must be valid JSON.');
        return;
      }
    }
    const payload = {
      code: form.code.trim(),
      name: form.name.trim(),
      description: form.description?.trim() || undefined,
      is_system: form.is_system,
      category: form.category || undefined,
      license_capability: license_capability ?? undefined,
    };
    setSaving(true);
    setError('');
    try {
      if (editing) {
        await adminSchemaApi.updateLicenseType(editing.id, payload);
      } else {
        await adminSchemaApi.createLicenseType(payload);
      }
      handleCloseModal();
      await loadList();
    } catch (err) {
      setError(err?.message || 'Failed to save license type');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!id || !window.confirm('Delete this license type? This will fail if any company license uses it.')) return;
    setDeleteId(id);
    setDeleting(true);
    setError('');
    try {
      await adminSchemaApi.deleteLicenseType(id);
      setDeleteId(null);
      await loadList();
    } catch (err) {
      setError(err?.message || 'Failed to delete license type');
      setDeleteId(null);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <h2 className="admin-title">License Types</h2>
      <p className="admin-subtitle">
        Manage license types used for company subscriptions (e.g. Admin, Platform, Community). Code must be unique.
      </p>

      {error && (
        <Alert variant="danger" onClose={() => setError('')} dismissible>
          {error}
        </Alert>
      )}

      <div className="mb-3">
        <Button className="admin-primary-btn" onClick={openCreate}>
          New License Type
        </Button>
      </div>

      {loading ? (
        <p className="admin-subtitle">Loading...</p>
      ) : (
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>Code</th>
              <th>Name</th>
              <th>Category</th>
              <th>Description</th>
              <th>System</th>
              <th style={{ width: 120 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.map((row) => (
              <tr key={row.id}>
                <td>{row.code || '—'}</td>
                <td>{row.name || '—'}</td>
                <td>{row.category || '—'}</td>
                <td>{row.description || '—'}</td>
                <td>{row.is_system ? 'Yes' : 'No'}</td>
                <td>
                  <Button variant="outline-primary" size="sm" className="me-1" onClick={() => openEdit(row)}>
                    <FaEdit />
                  </Button>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => handleDelete(row.id)}
                    disabled={deleting && deleteId === row.id}
                  >
                    <FaTrash />
                  </Button>
                </td>
              </tr>
            ))}
            {list.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center text-muted">
                  No license types. Create one to use in company subscriptions and pricebook entries.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      )}

      <Modal show={showModal} onHide={handleCloseModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>{editing ? 'Edit License Type' : 'New License Type'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Code</Form.Label>
              <Form.Control
                type="text"
                value={form.code}
                onChange={(e) => setForm((p) => ({ ...p, code: e.target.value }))}
                placeholder="e.g. PLATFORM"
                required
                disabled={!!editing}
              />
              {editing && <Form.Text className="text-muted">Code cannot be changed after create.</Form.Text>}
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                placeholder="e.g. Platform User"
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Category</Form.Label>
              <Form.Select
                value={form.category}
                onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))}
              >
                <option value="">— None —</option>
                <option value="USER">User</option>
                <option value="FEATURE">Feature</option>
              </Form.Select>
              <Form.Text className="text-muted">Optional: USER or FEATURE.</Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={form.description}
                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                placeholder="Optional"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>License capability (JSON)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={form.license_capability}
                onChange={(e) => setForm((p) => ({ ...p, license_capability: e.target.value }))}
                placeholder='Optional e.g. {"features": ["read", "write"]}'
                className="font-monospace"
              />
              <Form.Text className="text-muted">Optional JSON for capabilities/permissions.</Form.Text>
            </Form.Group>
            <Form.Check
              type="checkbox"
              label="System (seed/default type)"
              checked={form.is_system}
              onChange={(e) => setForm((p) => ({ ...p, is_system: e.target.checked }))}
            />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button type="submit" className="admin-primary-btn" disabled={saving}>
              {saving ? 'Saving…' : editing ? 'Save' : 'Create'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export default AdminLicenseTypes;
